import { useId } from 'react';
import { darken, lighten } from '@mui/material/styles';

/**
 * A rounded, dimensional smiley.
 *
 * Drawn rather than taken from the icon set or typed as an emoji, for two
 * reasons. MUI's `SentimentSatisfiedAlt` is a flat outline — correct for a
 * toolbar, but it reads as a symbol rather than a face. And an emoji (😊) is
 * fixed yellow and rendered by the OS, so it cannot turn green when the shift
 * starts and looks different on every phone.
 *
 * The roundness comes from four things stacked, which is what separates a
 * sphere from a circle:
 *
 *   1. A radial gradient offset up and left, so the light has a direction.
 *   2. A darker rim, thickest away from that light, giving the edge thickness.
 *   3. A soft specular highlight where the light lands.
 *   4. A drop shadow under the ball, so it sits above the page.
 *
 * Everything derives from one `colour`, so the caller changes the mood by
 * changing the hue and nothing else has to agree with it.
 */
export function SmileyFace({ colour, size = 40 }: { colour: string; size?: number }): React.JSX.Element {
    // Unique per instance: two of these on one page would otherwise share
    // gradient ids and the second would render with the first's colours.
    const uid = useId().replace(/:/g, '');
    const body = `body-${uid}`;
    const gloss = `gloss-${uid}`;
    const shadow = `shadow-${uid}`;

    const light = lighten(colour, 0.55);
    const dark = darken(colour, 0.3);
    const rim = darken(colour, 0.45);
    // Features are the base hue driven almost to black, so they read as part of
    // the same object rather than black dots stuck on it.
    const feature = darken(colour, 0.78);

    return (
        <svg width={size} height={size} viewBox="0 0 64 64" role="presentation" focusable="false">
            <defs>
                <radialGradient id={body} cx="34%" cy="27%" r="78%">
                    <stop offset="0%" stopColor={light} />
                    <stop offset="52%" stopColor={colour} />
                    <stop offset="100%" stopColor={dark} />
                </radialGradient>
                {/* White fading to nothing — a hard-edged ellipse would look
                    like a sticker rather than a reflection. */}
                <radialGradient id={gloss} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.62" />
                    <stop offset="70%" stopColor="#ffffff" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                </radialGradient>
                {/* Neutral and offset downward. Tinting it with the ball's own
                    colour and spreading it evenly made a glow, which reads as
                    the thing emitting light rather than sitting above a page. */}
                <filter id={shadow} x="-30%" y="-30%" width="160%" height="175%">
                    <feDropShadow dx="0" dy="2.4" stdDeviation="1.5" floodColor="#000000" floodOpacity="0.55" />
                </filter>
            </defs>

            <g filter={`url(#${shadow})`}>
                <circle cx="32" cy="32" r="29" fill={`url(#${body})`} />
                {/* Inside the edge, not on it, so the ball keeps its silhouette. */}
                <circle cx="32" cy="32" r="28" fill="none" stroke={rim} strokeOpacity="0.5" strokeWidth="1.6" />
            </g>

            <ellipse cx="23" cy="19" rx="14" ry="9.5" fill={`url(#${gloss})`} transform="rotate(-22 23 19)" />

            {/* Eyes, with a catchlight each — the small white dot is most of
                what makes a face look alive rather than printed. */}
            <ellipse cx="23" cy="27" rx="3.4" ry="4.6" fill={feature} />
            <ellipse cx="41" cy="27" rx="3.4" ry="4.6" fill={feature} />
            <circle cx="24.3" cy="25.2" r="1.25" fill="#ffffff" fillOpacity="0.85" />
            <circle cx="42.3" cy="25.2" r="1.25" fill="#ffffff" fillOpacity="0.85" />

            <path
                d="M21 38 Q32 49 43 38"
                fill="none"
                stroke={feature}
                strokeWidth="4.2"
                strokeLinecap="round"
            />
        </svg>
    );
}
